// App.js
import { BrowserRouter as Router, Route, Routes, useLocation, useParams, Navigate } from 'react-router-dom';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import Badge from 'react-bootstrap/Badge';
import { Spinner } from "react-bootstrap";
import config from "./devprodConfig";
import Sidebar from './components/Sidebar';
import ReportTemplate from './components/report/ReportTemplate';
import Kayttajahallinta from "./pages/Kayttajahallinta";
import LlmPromptit from "./pages/LlmPromptit";
import Tekstisuositukset from "./pages/Tekstisuositukset";
import Home from './pages/Home';
import Login from './pages/Login';
import SharePoint from './pages/Sharepoint';
import Logout from './pages/Logout';
import Profile from './pages/profile';
import Taloyhtiokortti from './pages/Taloyhtiokortti';
import Taloyhtiokortit from './pages/Taloyhtiokortit';
import AllReports from './pages/AllReports';
import Valmislauseet from './pages/Valmislauseet';
import Koho from './pages/Koho';
import MmlTileStats from './pages/MmlTileStats';
import AdminDashboard from './pages/AdminDashboard';
import DbViewerPage from './pages/DbViewerPage';
import KehityslistaPage from './components/Kehityslista/KehityslistaPage';
import { PTSProvider } from "./context/PTSContext";
import './App.css';
import axios from 'axios';
import { UserProvider } from './context/UserContext';
import { useUser } from "./context/UserContext";
import ZoomWrapper from './components/utils/ZoomWrapper';
import LexicalShowcase from './components/report/blocks/LexicalText/LexicalShowcase';
import Can from './permissions/Can';
import { PERMISSIONS } from './permissions/permissions';
import ProtectedRoute from './permissions/ProtectedRoute';
import ReportSidebar from './components/report/ReportSidebar';
import { ReportTemplateContainer } from './components/report/ReportTemplate/ReportTemplateContainer';
import { RakennusDataProvider } from './context/RakennusDataContext';
import { ImageQueueContext, ImageQueueProvider } from './context/ImageQueueContext';
import useDocumentTitle from './components/hooks/useDocumentTitle';

// Static browser-tab titles, keyed by path. Routes whose title depends on
// loaded data (the kiinteistö card + its active tab, the report's
// templateName) set their own title inside their own page component
// instead — listed here only so this effect skips them, since otherwise
// its own effect could run after the page's and clobber it with a generic
// title (React runs child effects before parent effects).
const STATIC_TITLES = [
  { test: (p) => p === '/login', title: 'Kirjaudu sisään' },
  { test: (p) => p === '/', title: 'Haku' },
  { test: (p) => p === '/about', title: 'Tietoa' },
  { test: (p) => p === '/contact', title: 'Yhteystiedot' },
  { test: (p) => p === '/profile', title: 'Omat tiedot' },
  { test: (p) => p === '/taloyhtiokortit', title: 'Kortit' },
  { test: (p) => p === '/llm-promptit', title: 'LLM-promptit' },
  { test: (p) => p === '/tekstisuositukset', title: 'Tekstisuositukset' },
  { test: (p) => p === '/kayttajahallinta', title: 'Hallinta' },
  { test: (p) => p === '/raportit', title: 'Raportit' },
  { test: (p) => p === '/valmislauseet', title: 'Valmislauseet' },
  { test: (p) => p === '/kehityslista', title: 'Kehityslista' },
  { test: (p) => p === '/koho', title: 'Koho' },
  { test: (p) => p.startsWith('/db-viewer/'), title: 'DB-katselin' },
  { test: (p) => p === '/sharepoint', title: 'SharePoint' },
  { test: (p) => p === '/admin', title: 'Admin' },
  { test: (p) => p === '/logout', title: 'Kirjaudu ulos' },
];

function staticTitleFor(pathname) {
  if (pathname.includes('/raportti/')) return undefined;
  if (/^\/taloyhtiokortti\/[^/]+$/.test(pathname)) return undefined;
  return STATIC_TITLES.find((entry) => entry.test(pathname))?.title;
}


function RateLimitToast() {
  const [toast, setToast] = useState(null); // { retryAfter, remaining }
  const timerRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      const retryAfter = e.detail?.retryAfter ?? 60;
      setToast({ remaining: retryAfter });
      clearInterval(timerRef.current);
      timerRef.current = setInterval(() => {
        setToast(prev => {
          if (!prev || prev.remaining <= 1) { clearInterval(timerRef.current); return null; }
          return { remaining: prev.remaining - 1 };
        });
      }, 1000);
    };
    window.addEventListener('ktp:ratelimit', handler);
    return () => { window.removeEventListener('ktp:ratelimit', handler); clearInterval(timerRef.current); };
  }, []);

  if (!toast) return null;

  return (
    <div style={{
      position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)',
      zIndex: 99999, background: '#dc3545', color: '#fff', borderRadius: 8,
      padding: '12px 24px', boxShadow: '0 4px 16px rgba(0,0,0,0.25)',
      display: 'flex', alignItems: 'center', gap: 12, fontSize: '0.95rem',
    }}>
      <i className="bi bi-exclamation-triangle-fill" />
      <span>Liian monta pyyntöä — yritä uudelleen {toast.remaining} sekunnin kuluttua.</span>
    </div>
  );
}

function App() {
  return (
    <UserProvider>
      <ImageQueueProvider>
          <Router>
              <RateLimitToast />
              <Layout />
          </Router>
      </ImageQueueProvider>
    </UserProvider>
  );
}


// Layout handles sidebar and section state
function Layout() {
  const location = useLocation();
  const isReportRoute = location.pathname.includes('/raportti/') || location.pathname.includes('/db-viewer/');

  useDocumentTitle(staticTitleFor(location.pathname));

  const sidebar = isReportRoute ? <></> : <Sidebar />;
  const defaultZoom = 1

  return (
    <div className="layout d-flex">
      {sidebar}
      <div className="app-container flex-fill">
        <Routes>
          {/* Public login page */}
          <Route path="/login" element={<Login />} />

          {/* Protected pages */}
          <Route
            path="/"
            element={
              <ProtectedRoute permission={PERMISSIONS.GENERAL.VIEW_SEARCH}>
                <ZoomWrapper zoom={defaultZoom}>
                  <Home />
                </ZoomWrapper>
              </ProtectedRoute>
            }
          />
          <Route
            path="/about"
            element={
              <ProtectedRoute>
                <ZoomWrapper zoom={defaultZoom}>
                  <About />
                </ZoomWrapper>
              </ProtectedRoute>
            }
          />
          <Route
            path="/contact"
            element={
              <ProtectedRoute>
               <ZoomWrapper zoom={defaultZoom}>
                  <Contact />
                </ZoomWrapper>
              </ProtectedRoute>
            }
          />
          <Route
            path="/profile"
            element={
              <ProtectedRoute>
                <ZoomWrapper zoom={defaultZoom}>
                  <Profile />
                </ZoomWrapper>
              </ProtectedRoute>
            }
          />
            <Route
              path="/taloyhtiokortit"
              element={
                <ProtectedRoute permission={PERMISSIONS.GENERAL.VIEW_TALOYHTIOKORTIT}>
                  
                    <ZoomWrapper zoom={defaultZoom}>
                      <Taloyhtiokortit />
                    </ZoomWrapper>

                </ProtectedRoute>
              }
            />
          <Route
            path="/taloyhtiokortti/:id"
            element={
              <ProtectedRoute permission={PERMISSIONS.GENERAL.VIEW_TALOYHTIOKORTIT}>
                <RakennusDataProvider>
                    <PTSProvider>
                      <ZoomWrapper zoom={defaultZoom}>
                        <Taloyhtiokortti />
                      </ZoomWrapper>
                    </PTSProvider>
                </RakennusDataProvider>
              </ProtectedRoute>
            }
          />
          <Route
            path="/taloyhtiokortti/:id/raportti/:templateName/:reportId/:section/:sectionId/:subsectionId?"
            element={
              <ProtectedRoute>
                <RakennusDataProvider>
                    <PTSProvider>
                      <ReportTemplateContainer></ReportTemplateContainer>
                    </PTSProvider>
                </RakennusDataProvider>
              </ProtectedRoute>
            }
          />
          <Route
            path="/taloyhtiokortti/raportti/:templateName/:reportId/:section/:sectionId/:subsectionId?"
            element={
              <ProtectedRoute>
                <PTSProvider>
                  <ReportTemplateContainer></ReportTemplateContainer>
                </PTSProvider>
              </ProtectedRoute>
            }
          />
          <Route
            path="/llm-promptit"
            element={
              <ProtectedRoute permission={PERMISSIONS.REPORTS.AI_GENERATE}>
                <ZoomWrapper zoom={defaultZoom}>
                  <LlmPromptit />
                </ZoomWrapper>
              </ProtectedRoute>
            }
          />
          <Route
            path="/tekstisuositukset"
            element={
              <ProtectedRoute permission={PERMISSIONS.REPORTS.MANAGE_TEXT_RECOMMENDATIONS}>
                <ZoomWrapper zoom={defaultZoom}>
                  <Tekstisuositukset />
                </ZoomWrapper>
              </ProtectedRoute>
            }
          />
          <Route
            path="/kayttajahallinta"
            element={
              <ProtectedRoute permission={PERMISSIONS.GENERAL.MANAGE_USERS}>
                <AdminRoute>
                  <ZoomWrapper zoom={defaultZoom}>
                    <Kayttajahallinta />
                  </ZoomWrapper>
                </AdminRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path="/raportit"
            element={
              <ProtectedRoute>
                <ZoomWrapper zoom={defaultZoom}>
                  <AllReports />
                </ZoomWrapper>
              </ProtectedRoute>
            }
          />
          {/* Admin-only for now — the feature is still shaking out; widen
              this back to permission-only (like /raportit's templates tab
              used to be) once it's ready for non-admins. */}
          <Route
            path="/valmislauseet"
            element={
              <ProtectedRoute permission={PERMISSIONS.REPORTS.VIEW_TEMPLATES}>
                <AdminRoute>
                  <ZoomWrapper zoom={defaultZoom}>
                    <Valmislauseet />
                  </ZoomWrapper>
                </AdminRoute>
              </ProtectedRoute>
            }
          />

          <Route
            path="/kehityslista"
            element={
              <ProtectedRoute permission={PERMISSIONS.KEHITYSLISTA.VIEW}>
                <KehityslistaPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/koho"
            element={
              <ProtectedRoute>
                <ZoomWrapper zoom={defaultZoom}>
                  <Koho />
                </ZoomWrapper>
              </ProtectedRoute>
            }
          />
          <Route
            path="/db-viewer/:ptsProjectId"
            element={
              <ProtectedRoute>
                <DbViewerPage />
              </ProtectedRoute>
            }
          />
<Route
  path="/sharepoint"
  element={
    <ProtectedRoute>
      <ZoomWrapper zoom={defaultZoom}>
        <SharePoint />
      </ZoomWrapper>
    </ProtectedRoute>
  }
/>
          <Route
            path="/admin"
            element={
              <ProtectedRoute permission={PERMISSIONS.GENERAL.MANAGE_USERS}>
                <AdminRoute>
                  <ZoomWrapper zoom={defaultZoom}>
                    <AdminDashboard />
                  </ZoomWrapper>
                </AdminRoute>
              </ProtectedRoute>
            }
          />
          {/* Logout page can be public */}
          <Route
            path="/logout"
            element={
              <ZoomWrapper zoom={defaultZoom}>
                <Logout />
              </ZoomWrapper>
            }
          />
        </Routes>
      </div>
    </div>
  );
}



function useAuth() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function checkAuth() {
      try {
        // Calls your backend /me endpoint that uses the cookie
        await axios.get(`${process.env.REACT_APP_PROD_API_URL}/me`, {
          withCredentials: true,
        });
        setIsAuthenticated(true);
      } catch (err) {
        setIsAuthenticated(false);
      } finally {
        setLoading(false);
      }
    }
    checkAuth();
  }, []);

  return { isAuthenticated, loading };
}

function AdminRoute({ children }) {
  const { localUser, loading } = useUser();

  if (loading) {
    return (
      <div className="text-center mt-5">
        <Spinner animation="border" />
      </div>
    );
  }

  if (!localUser || localUser.rooli !== "admin") {
    return <Navigate to="/profile" replace />;
  }

  return children;
}

function ScrollToTop() {
  const location = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location]);
  return null;
}

function About() {
  const [content, setContent] = useState('');
  const textareaRef = useRef(null);
  const minHeightRef = useRef(null);

  useLayoutEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const computedStyle = window.getComputedStyle(textarea);
    const lineHeight = parseFloat(computedStyle.lineHeight);
    const currentHeight = parseFloat(computedStyle.height);

    // cache minimum height
    if (!minHeightRef.current) {
      minHeightRef.current = currentHeight;
    }

    // calculate scroll difference
    const scrollDiff = textarea.scrollHeight - textarea.offsetHeight;
    const newHeight =
      scrollDiff >= 0
        ? currentHeight + scrollDiff
        : Math.max(minHeightRef.current, currentHeight + scrollDiff);

    textarea.style.height = `${newHeight}px`;
  }, [content]);

  return (
    <div style={{ padding: '2rem', maxWidth: 1200 }}>
      <h2 className="text-primary">Ohjeet</h2>
      <h4 className="text-primary">Tekstikenttä testaus...</h4>
      <LexicalShowcase></LexicalShowcase>

    </div>
  );
}
function Contact() {
  return (
    <h2 className="text-primary">
      Ota yhteyttä <Badge bg="secondary">Tulossa</Badge>
    </h2>
  );
}



export default App;
